import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setCurrency } from './utils/money';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// Safe localStorage wrappers, iOS Safari private mode throws SecurityError
const storage = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, val) => { try { localStorage.setItem(key, val); } catch {} },
  remove: (key) => { try { localStorage.removeItem(key); } catch {} },
};

// Send auth token with every API request automatically
axios.interceptors.request.use((config) => {
  const token = storage.get('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Account context (team seats): which account this person is acting in -
  // "personal" or a team owner's id. Absent => backend defaults to the team they
  // most recently accepted. Set by the account switcher / on invite accept.
  const ctx = storage.get('scn-context');
  if (ctx) {
    config.headers['x-scn-context'] = ctx;
  }
  return config;
});

// When the server returns 401, the token has expired or is invalid.
// Clear state so ProtectedRoute redirects to /login automatically.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      storage.remove('token');
      // Dispatch a custom event so AuthProvider can clear user state
      window.dispatchEvent(new Event('scn-auth-expired'));
    }
    // 403 permission_denied = a team member hit an action their role doesn't allow.
    // Surface the friendly reason (data.message) into data.error so the many handlers
    // that toast `err.response.data.error` show "Permission not allowed..." instead of
    // the raw code. No handler keys logic on this new code, so the rewrite is safe.
    if (error.response?.status === 403 && error.response?.data?.error === 'permission_denied') {
      try { error.response.data.error = error.response.data.message || 'Permission not allowed for your team role.'; } catch {}
    }
    // 402 = read-only billing hold: a write was blocked because their card failed.
    // Send them straight to the Stripe billing portal to fix it (guarded so many
    // simultaneous 402s trigger only one redirect). Falls back to the billing tab.
    if (error.response?.status === 402 && error.response?.data?.error === 'payment_locked' && !window.__scnBillingRedirect) {
      window.__scnBillingRedirect = true;
      axios.post(`${API}/stripe/billing-portal`)
        .then(r => { window.location.href = r.data?.url || '/settings?tab=subscription'; })
        .catch(() => { window.location.href = '/settings?tab=subscription'; });
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = storage.get('token');
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setUser(res.data))
        .catch(() => {
          storage.remove('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Apply the dealer's display currency app-wide whenever the user loads/changes.
  // Guard on a defined value so a login response that omits currency doesn't
  // reset a non-USD dealer back to USD (localStorage keeps the last value, and
  // /auth/me on the next load re-confirms it authoritatively).
  useEffect(() => {
    if (user && user.currency) setCurrency(user.currency);
  }, [user]);

  // Clear user state when the response interceptor detects a 401 (expired token)
  useEffect(() => {
    const handleExpired = () => setUser(null);
    window.addEventListener('scn-auth-expired', handleExpired);
    return () => window.removeEventListener('scn-auth-expired', handleExpired);
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.twoFactorRequired) return res.data; // caller shows the code step; no token yet
    storage.set('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  // Step 2 of login when 2FA is on: exchange the challenge + emailed code for a session.
  const verifyTwoFactor = async (challenge, code) => {
    const res = await axios.post(`${API}/auth/login/2fa`, { challenge, code });
    storage.set('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name, utmData) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, ...utmData });
    storage.set('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const googleLogin = async (credential, utmData) => {
    const res = await axios.post(`${API}/auth/google`, { credential, ...utmData });
    if (res.data.twoFactorRequired) return res.data; // caller shows the code step
    storage.set('token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    storage.remove('token');
    storage.remove('scn-context'); // don't carry one person's account context to the next login
    storage.remove('scn:mscan');   // don't let a remembered phone-scan session cross users on a shared browser
    setUser(null);
  };

  // Re-fetch the current user (e.g. after returning from Stripe so the trial
  // wall sees the fresh subscriptionEntitled and lets them through).
  const refreshUser = async () => {
    const token = storage.get('token');
    if (!token) return null;
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch { return null; }
  };

  // Permission check for the UI. Owners and people in their personal account
  // (isTeamMember false) can do everything; a team member is limited to the
  // toggles the owner granted. Mirrors the server's requirePermission so the UI
  // only HIDES what the backend would also 403. Keys: viewFinancials,
  // changePricing, deleteBulk, manageChannels, exportData.
  const can = (key) => {
    if (!user) return false;
    if (!user.isTeamMember) return true;
    return !!(user.teamPermissions && user.teamPermissions[key] === true);
  };
  const isTeamMember = !!user?.isTeamMember;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, verifyTwoFactor, logout, refreshUser, can, isTeamMember }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}